import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useParams, useNavigate } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { TriangleAlert, PackageSearch } from "lucide-react";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "../../Page/Header/Header";
import ProductsLayout from "./ProductsLayout";
import ProductCard from "../../Components/ProductCard";
import fallbackImage from "../../assets/brand-default.jpg";
import { useDispatch, useSelector } from "react-redux";
import Footer from "../../Components/Footer/Footer";
import {
	fetchWishlist,
	addItemToWishlist,
	removeItemFromWishlist,
} from "../../redux/thunks/wishlistThunks";
import { updateItemQuantity, addToCart, fetchCart } from "../../redux/thunks/cartThunks";
import { globalLogout } from "../../Utils/Logout";

/* ---------- filter helpers (unchanged logic) ---------- */
const getUnique = (arr, key) => [
	...new Set(
		arr
			.map((item) => {
				if (key === "brand") return item.brand?.name;
				if (key === "price") return item.variants?.map((v) => v.price);
				if (key === "ratings") return Math.floor(item.averageRating || 0);
				return null;
			})
			.flat()
			.filter(Boolean)
	),
];

const getPriceRanges = (prices) => {
	if (!prices.length) return [];
	const sorted = [...prices].sort((a, b) => a - b);
	const min = sorted[0];
	const max = sorted[sorted.length - 1];
	if (min === max) return [{ label: `₹${min}`, min, max }];
	const step = Math.ceil((max - min) / 4);
	const ranges = [];
	let start = min;
	for (let i = 0; i < 4; i++) {
		const end = i === 3 ? max : start + step - 1;
		ranges.push({ label: `₹${start} - ₹${end}`, min: start, max: end });
		start = end + 1;
	}
	return ranges;
};

// 1 2 3 ... 9 10 style page list
const getPageNumbers = (current, total) => {
	if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
	const pages = [1];
	const start = Math.max(2, current - 1);
	const end = Math.min(total - 1, current + 1);
	if (start > 2) pages.push("...start");
	for (let i = start; i <= end; i++) pages.push(i);
	if (end < total - 1) pages.push("...end");
	pages.push(total);
	return pages;
};

const ProductsBySubCategory = () => {
	const { slug } = useParams();
	const [products, setProducts] = useState([]);
	const [filteredProducts, setFilteredProducts] = useState([]);
	const [category, setCategory] = useState("Products");
	const [loading, setLoading] = useState(false);
	const [sortOption, setSortOption] = useState("featured");
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(12);
	const [loadingWishlistItems, setLoadingWishlistItems] = useState({});
	const [loadingCartItems, setLoadingCartItems] = useState({});
	const [imageErrors, setImageErrors] = useState({});
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [allCategories, setAllCategories] = useState([]);
	const [subcategories, setSubcategories] = useState([]);
	const [selectedSubcategory, setSelectedSubcategory] = useState("");

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const token = Cookies.get("ecom_user_token");
	const { items: wishlist } = useSelector((state) => state.wishlist);
	const cartItems = useSelector((state) => state.cart.items);

	/* ---------- categories ---------- */
	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`);
				setAllCategories(res.data.categories);
			} catch (error) {
				globalLogout(error);
			}
		};
		fetchCategories();
	}, []);

	const findSubcategories = (categories, slug) => {
		for (const category of categories) {
			if (category.slug === slug) {
				setSelectedSubcategory(category.id);
				return category.subcategories.filter((subcat) => subcat.published).map((subcat) => ({ ...subcat }));
			}
			if (category.subcategories?.length > 0) {
				const found = findSubcategories(category.subcategories, slug);
				if (found.length) return found;
			}
		}
		return [];
	};

	useEffect(() => {
		if (!allCategories.length || !slug) return;
		setSubcategories(findSubcategories(allCategories, slug));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allCategories, slug]);

	/* ---------- cart + wishlist ---------- */
	useEffect(() => {
		if (token) {
			dispatch(fetchWishlist());
			dispatch(fetchCart());
		}
	}, [token, dispatch]);

	/* ---------- products ---------- */
	useEffect(() => {
		if (!selectedSubcategory) return;

		const fetchData = async () => {
			setLoading(true);
			try {
				const res = await axios.get(
					`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${selectedSubcategory}`
				);

				const list = res.data.products
					.filter(
						(p) =>
							p.published &&
							p.adminPublished &&
							p.brand?.published &&
							p.brand?.adminPublished &&
							p.category?.published
					)
					.map((p) => {
						const activeVar = p.variants?.find((v) => v.status === "active");
						const outOfStockVar = p.variants?.find((v) => v.status === "out_of_stock");
						const displayVariant = activeVar || outOfStockVar;
						if (!displayVariant) return null;
						return {
							...p,
							variants: [displayVariant],
							isInStock:
								displayVariant.status === "active" &&
								(displayVariant.stock > 0 || displayVariant.stock === undefined),
						};
					})
					.filter(Boolean);

				setProducts(list);
				setFilteredProducts(list);
				setCategory(
					res.data.products[0]?.category?.name || res.data.products[0]?.subcategory?.name || "Products"
				);
			} catch (e) {
				globalLogout(e);
				if (e.response?.status !== 404) toast.error("Failed to load products");
				setProducts([]);
				setFilteredProducts([]);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [selectedSubcategory]);

	const handleSubcategorySelect = (subcategoryId) => {
		setSelectedSubcategory(subcategoryId);
		setCurrentPage(1);
	};

	/* ---------- filters ---------- */
	const brands = useMemo(() => getUnique(products, "brand"), [products]);
	const prices = useMemo(() => getUnique(products, "price"), [products]);
	const priceRanges = useMemo(() => getPriceRanges(prices), [prices]);
	const ratings = useMemo(
		() => [5, 4, 3, 2, 1].filter((r) => getUnique(products, "ratings").includes(r)),
		[products]
	);

	const onFilter = (filters) => {
		let output = [...products];
		if (filters.brands?.length) output = output.filter((p) => filters.brands.includes(p.brand?.name));
		if (filters.price) {
			output = output.filter((p) =>
				p.variants.some((v) => v.price >= filters.price.min && v.price <= filters.price.max)
			);
		}
		if (filters.rating) {
			output = output.filter((p) => Math.floor(p.averageRating || 0) >= filters.rating);
		}
		setFilteredProducts(output);
		setCurrentPage(1);
	};

	const onReset = () => {
		setSubcategories(findSubcategories(allCategories, slug));
		setFilteredProducts(products);
		setCurrentPage(1);
	};

	/* ---------- sort + pagination ---------- */
	const sortedProducts = useMemo(() => {
		const sorted = [...filteredProducts];
		switch (sortOption) {
			case "price-low":
				return sorted.sort(
					(a, b) =>
						Math.min(...a.variants.map((v) => v.price)) - Math.min(...b.variants.map((v) => v.price))
				);
			case "price-high":
				return sorted.sort(
					(a, b) =>
						Math.max(...b.variants.map((v) => v.price)) - Math.max(...a.variants.map((v) => v.price))
				);
			case "rating":
				return sorted.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
			case "newest":
				return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
			default:
				return sorted;
		}
	}, [filteredProducts, sortOption]);

	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
	const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

	const goToPage = (page) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	};

	/* ---------- images ---------- */
	const getProductImage = (product) => {
		if (imageErrors[product._id]) return fallbackImage;
		return product.variants?.[0]?.images?.[0] || product.images?.[0] || fallbackImage;
	};

	/* ---------- cart handlers ---------- */
	const getCartItem = (product) =>
		cartItems.find(
			(item) =>
				item.productId === product._id &&
				(product.variants?.length > 0 ? item.variantId === product.variants[0]._id : true)
		);

	const handleAddToCart = async (product) => {
		if (!token) {
			setShowLoginModal(true);
			return;
		}
		if (!product.isInStock) {
			toast.warning("This item is out of stock");
			return;
		}
		try {
			setLoadingCartItems((prev) => ({ ...prev, [product._id]: true }));
			await dispatch(
				addToCart({
					productId: product._id,
					variantId: product.variants?.[0]?._id,
					quantity: 1,
				})
			);
			toast.success("Added to cart!");
		} catch (error) {
			console.error("Add to cart failed:", error);
			toast.error(error.response?.data?.message || "Failed to add to cart");
		} finally {
			setLoadingCartItems((prev) => ({ ...prev, [product._id]: false }));
		}
	};

	const handleQuantityChange = async (product, newQuantity) => {
		if (newQuantity < 0) return;
		try {
			setLoadingCartItems((prev) => ({ ...prev, [product._id]: true }));
			await dispatch(
				updateItemQuantity({
					productId: product._id,
					variantId: product.variants?.[0]?._id,
					quantity: newQuantity,
				})
			);
		} catch (error) {
			console.error("Error updating quantity:", error);
			toast.error(error.response?.data?.message || "Failed to update cart");
		} finally {
			setLoadingCartItems((prev) => ({ ...prev, [product._id]: false }));
		}
	};

	/* ---------- wishlist ---------- */
	const isWishlisted = (productId, variantId) =>
		wishlist.some((item) => item?.productId?._id === productId && item?.variantId === variantId);

	const toggleWishlist = async (productId, variantId) => {
		if (!token) {
			toast.info("Please login to manage your wishlist");
			navigate("/login");
			return;
		}
		try {
			setLoadingWishlistItems((prev) => ({ ...prev, [productId]: true }));
			if (isWishlisted(productId, variantId)) {
				await dispatch(removeItemFromWishlist(productId, variantId));
				toast.success("Removed from wishlist");
			} else {
				await dispatch(addItemToWishlist(productId, variantId));
				toast.success("Added to wishlist");
			}
		} catch (error) {
			console.error("Wishlist operation failed:", error);
			toast.error("Failed to update wishlist. Please try again.");
		} finally {
			setLoadingWishlistItems((prev) => ({ ...prev, [productId]: false }));
		}
	};

	/* ---------- pagination bar ---------- */
	const renderPagination = () => {
		if (totalPages <= 1) return null;
		const pageBtn = "min-w-[2rem] h-8 px-2 rounded-md text-sm border transition-colors";

		return (
			<div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8">
				<span className="text-sm text-gray-600">
					Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredProducts.length)} of{" "}
					{filteredProducts.length} products
				</span>

				<div className="flex items-center gap-2 flex-wrap justify-center">
					<select
						value={itemsPerPage}
						onChange={(e) => {
							setItemsPerPage(parseInt(e.target.value));
							setCurrentPage(1);
						}}
						className="h-8 border border-gray-300 rounded-md px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-800/30 focus:border-yellow-800"
					>
						{[8, 12, 16, 20, 24].map((n) => (
							<option key={n} value={n}>
								{n} per page
							</option>
						))}
					</select>

					<button
						type="button"
						aria-label="Previous page"
						onClick={() => goToPage(currentPage - 1)}
						disabled={currentPage === 1}
						className={`${pageBtn} border-gray-300 text-gray-700 hover:bg-yellow-50 disabled:opacity-40 disabled:cursor-not-allowed`}
					>
						<FiChevronLeft className="mx-auto" />
					</button>

					{getPageNumbers(currentPage, totalPages).map((p) =>
						typeof p === "string" ? (
							<span key={p} className="px-1 text-gray-400">
								...
							</span>
						) : (
							<button
								key={p}
								type="button"
								onClick={() => goToPage(p)}
								className={`${pageBtn} ${currentPage === p
										? "bg-yellow-800 border-yellow-800 text-white font-semibold"
										: "border-gray-300 text-gray-700 hover:bg-yellow-50"
									}`}
							>
								{p}
							</button>
						)
					)}

					<button
						type="button"
						aria-label="Next page"
						onClick={() => goToPage(currentPage + 1)}
						disabled={currentPage === totalPages}
						className={`${pageBtn} border-gray-300 text-gray-700 hover:bg-yellow-50 disabled:opacity-40 disabled:cursor-not-allowed`}
					>
						<FiChevronRight className="mx-auto" />
					</button>
				</div>
			</div>
		);
	};

	/* ---------- render ---------- */
	return (
		<>
			<Header />
			<ProductsLayout
				products={products}
				filters={{ brands, priceRanges, ratings }}
				onFilter={onFilter}
				onReset={onReset}
				subcategories={subcategories}
				onSubcategorySelect={handleSubcategorySelect}
				selectedSubcategory={selectedSubcategory}
			>
				{/* Login prompt */}
				{showLoginModal && (
					<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
						<div className="bg-white rounded-lg p-5 md:p-6 max-w-sm w-full">
							<div className="flex items-center justify-center pb-2">
								<TriangleAlert className="w-14 h-14 text-yellow-500" />
							</div>
							<h3 className="text-lg text-center font-semibold text-gray-900 mb-2">Login required</h3>
							<p className="text-gray-600 mb-5 text-sm text-center">
								You need to login to add items to your cart. Would you like to login now?
							</p>
							<div className="flex gap-3">
								<button
									onClick={() => setShowLoginModal(false)}
									className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
								>
									Cancel
								</button>
								<button
									onClick={() => {
										setShowLoginModal(false);
										navigate("/login", { state: { from: window.location.pathname } });
									}}
									className="flex-1 px-4 py-2 bg-yellow-800 text-white rounded-md hover:bg-yellow-900 text-sm font-medium"
								>
									Login
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Title + sort */}
				<div className="flex items-center justify-between gap-3 mb-4">
					<div className="min-w-0">
						<h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{category}</h1>
						<p className="text-xs text-gray-500 mt-0.5">
							{filteredProducts.length} {filteredProducts.length === 1 ? "item" : "items"}
						</p>
					</div>

					<div className="flex items-center gap-2 shrink-0">
						<label htmlFor="sort" className="hidden sm:block text-sm text-gray-600">
							Sort by
						</label>
						<select
							id="sort"
							value={sortOption}
							onChange={(e) => setSortOption(e.target.value)}
							className="h-9 border border-gray-300 rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-800/30 focus:border-yellow-800"
						>
							<option value="featured">Featured</option>
							<option value="price-low">Price: Low to High</option>
							<option value="price-high">Price: High to Low</option>
							<option value="rating">Top Rated</option>
							<option value="newest">Newest Arrivals</option>
						</select>
					</div>
				</div>

				{loading ? (
					<div className="flex justify-center items-center h-64">
						<PulseLoader color="#854d0e" size={12} />
					</div>
				) : filteredProducts.length === 0 ? (
					<div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
						<PackageSearch className="mx-auto h-16 w-16 text-gray-300" />
						<h3 className="mt-4 text-base font-semibold text-gray-900">No products found</h3>
						<p className="mt-1 text-sm text-gray-500">
							There are currently no products matching your filters
						</p>
						<button
							type="button"
							onClick={onReset}
							className="mt-5 inline-flex items-center px-5 py-2 text-sm font-semibold rounded-md bg-yellow-800 text-white hover:bg-yellow-900"
						>
							Reset Filters
						</button>
					</div>
				) : (
					<>
						<div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
							{currentItems.map((product) => {
								const variantId = product.variants?.[0]?._id;
								return (
									<ProductCard
										key={product._id}
										product={product}
										imageSrc={getProductImage(product)}
										onImageError={() => setImageErrors((prev) => ({ ...prev, [product._id]: true }))}
										showWishlist={!!token}
										isWishlisted={isWishlisted(product._id, variantId)}
										wishlistLoading={!!loadingWishlistItems[product._id]}
										cartItem={getCartItem(product)}
										cartLoading={!!loadingCartItems[product._id]}
										onToggleWishlist={toggleWishlist}
										onAddToCart={handleAddToCart}
										onQuantityChange={handleQuantityChange}
									/>
								);
							})}
						</div>
						{renderPagination()}
					</>
				)}
			</ProductsLayout>
			<Footer />
		</>
	);
};

export default ProductsBySubCategory;